import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getSubdomain } from '../utils/tenantUtils';
import { tenantService, TenantInfo } from '../services/tenantService';

// TenantInfo interface is now imported from tenantService

// Define context type
interface TenantContextType {
  tenant: TenantInfo | null;
  loading: boolean;
  error: string | null;
}

// Create the context with default values
const TenantContext = createContext<TenantContextType>({
  tenant: null,
  loading: true,
  error: null
});

// Custom hook for using the tenant context
export const useTenant = () => useContext(TenantContext);

// Provider component
interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTenantInfo = async () => {
      const subdomain = getSubdomain();
      
      // If no subdomain, we're on the main domain
      if (!subdomain) {
        setLoading(false);
        return;
      }
      
      try {
        // Fetch tenant information using the tenant service
        const tenantInfo = await tenantService.getTenantInfo(subdomain);
        setTenant(tenantInfo);
      } catch (err) {
        console.error('Error fetching tenant info:', err);
        setError('Failed to load tenant information');
      } finally {
        setLoading(false);
      }
    };

    fetchTenantInfo();
  }, []);

  return (
    <TenantContext.Provider value={{ tenant, loading, error }}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
